import { InputHTMLAttributes, ReactNode, RefObject, useEffect, useMemo, useRef, useState } from "react"
import { useFormikContext } from "formik"
import * as Yup from "yup"
import { makeStyles } from "tss-react/mui"
import { Box, ButtonBase, Typography } from "@mui/material"
import { faPenToSquare, faUser } from "@fortawesome/pro-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

import { MAX_EMAIL_LENGTH, MAX_PHONE_LENGTH, PHONE_REGEX } from "../../../../../../constants/inputValidationDefaults"
import { useAppLocalization } from "../../../../../../modules/localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import type { TranslationKey } from "../../../../../../modules/localization/translations"
import { useAppSelector } from "../../../../../../modules/store/storeHooks"
import { selectLoginUser } from "../../../../../../modules/users/userSelectors"
import type { User } from "../../../../../../modules/users/userTypes"
import { StandardInlineEditPopover } from "../../../../dialogs/StandardInlineEditPopover"

import type { CheckoutFormValues } from "./takeawayCheckoutLib"
import { TakeawayCheckoutSectionHeader } from "./TakeawayCheckoutSectionHeader"

const useStyles = makeStyles()((theme) => ({
    rows: {
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
    },
    row: {
        display: "flex",
        flexWrap: "wrap",
        gap: ".25rem",
        padding: "0 1rem",
        justifyContent: "space-between",
        alignItems: "center",
        color: theme.palette.tailwind.slate[950],
    },
    value: {
        color: theme.palette.tailwind.slate[950],
    },
    editableValue: {
        display: "inline-flex",
        alignItems: "center",
        gap: ".5rem",
        padding: ".125rem 0",
        borderRadius: ".25rem",
        background: "transparent",
        transition: "background .2s ease, padding .2s ease",
        cursor: "pointer",
        "&:hover, &:focus-visible": {
            padding: ".125rem .375rem",
            background: theme.palette.tailwind.blue[50],
        },
    },
    editableValueActive: {
        padding: ".125rem .375rem",
        background: theme.palette.tailwind.blue[50],
    },
    editIconWrap: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: "1rem",
        height: "1rem",
        flexShrink: 0,
    },
    editIcon: {
        width: "1rem",
        height: "1rem",
        fontSize: "1rem",
        color: theme.palette.tailwind.slate[400],
    },
}))

function hasAnyInfo(user: User | undefined | null) {
    return Boolean(user?.displayName || user?.email || user?.phoneNumber || user?.mobileNumber)
}

export function useHasTakeawayPersonalInfo(onBehalfUser?: User | null) {
    const loginUser = useAppSelector(selectLoginUser)
    return hasAnyInfo(onBehalfUser ?? loginUser)
}

enum EditableField {
    EMAIL = "email",
    PHONE = "phone",
}

type EditFieldConfig = Readonly<{
    formField: keyof CheckoutFormValues
    labelKey: TranslationKey
    inputProps: InputHTMLAttributes<HTMLInputElement>
}>

const EDIT_FIELD_CONFIG = {
    [EditableField.EMAIL]: {
        formField: "customerEmail",
        labelKey: "takeaway-confirm-order-personal-info-edit-email-field",
        inputProps: { maxLength: MAX_EMAIL_LENGTH, inputMode: "email" },
    },
    [EditableField.PHONE]: {
        formField: "customerPhone",
        labelKey: "takeaway-confirm-order-personal-info-edit-phone-field",
        inputProps: { maxLength: MAX_PHONE_LENGTH, inputMode: "tel" },
    },
} satisfies Record<EditableField, EditFieldConfig>

type EditableInfoRowProps = Readonly<{
    labelNode: ReactNode
    value: string
    isActive: boolean
    anchorRef: RefObject<HTMLButtonElement>
    onClick: () => void
}>

function EditableInfoRow({ labelNode, value, isActive, anchorRef, onClick }: EditableInfoRowProps) {
    const { classes, cx } = useStyles()
    return (
        <Box className={classes.row}>
            {labelNode}
            <ButtonBase
                ref={anchorRef}
                className={cx(classes.editableValue, isActive && classes.editableValueActive)}
                onClick={onClick}
            >
                <Typography variant="body1" className={classes.value}>
                    {value}
                </Typography>
                <Box className={classes.editIconWrap}>
                    <FontAwesomeIcon icon={faPenToSquare} className={classes.editIcon} />
                </Box>
            </ButtonBase>
        </Box>
    )
}

type Props = Readonly<{
    onBehalfUser?: User | null
}>

export function TakeawayCheckoutPersonalInfoSection({ onBehalfUser }: Props = {}) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const loginUser = useAppSelector(selectLoginUser)
    const user = onBehalfUser ?? loginUser
    const { values, setFieldValue } = useFormikContext<CheckoutFormValues>()

    const emailAnchorRef = useRef<HTMLButtonElement>(null)
    const phoneAnchorRef = useRef<HTMLButtonElement>(null)
    const [editingField, setEditingField] = useState<EditableField | null>(null)
    const [draftValue, setDraftValue] = useState("")
    const [showError, setShowError] = useState(false)

    const emailSchema = useMemo(
        () =>
            Yup.string()
                .max(MAX_EMAIL_LENGTH)
                .email(l10n.getString("takeaway-confirm-order-personal-info-edit-email-invalid")),
        [l10n],
    )
    const phoneSchema = useMemo(
        () =>
            Yup.string()
                .max(MAX_PHONE_LENGTH)
                .matches(PHONE_REGEX, {
                    message: l10n.getString("takeaway-confirm-order-personal-info-edit-phone-invalid"),
                    excludeEmptyString: true,
                }),
        [l10n],
    )

    const draftError = useMemo(() => {
        if (!editingField) return undefined
        const trimmed = draftValue.trim()
        if (!trimmed) return l10n.getString("required-field")
        const schema = editingField === EditableField.EMAIL ? emailSchema : phoneSchema
        try {
            schema.validateSync(trimmed)
            return undefined
        } catch (error) {
            if (error instanceof Yup.ValidationError) return error.message
            throw error
        }
    }, [editingField, draftValue, emailSchema, phoneSchema, l10n])

    useEffect(() => {
        if (editingField === null) setShowError(false)
    }, [editingField])

    const name = user?.displayName
    const email = values.customerEmail
    const phone = values.customerPhone

    if (!name && !email && !phone) return null

    function openEdit(field: EditableField, currentValue: string) {
        setEditingField(field)
        setDraftValue(currentValue)
        setShowError(false)
    }

    function closeEdit() {
        setEditingField(null)
    }

    function saveEdit() {
        if (!editingField) return
        if (draftError) {
            setShowError(true)
            return
        }
        setFieldValue(EDIT_FIELD_CONFIG[editingField].formField, draftValue.trim())
        setEditingField(null)
    }

    const activeConfig = editingField ? EDIT_FIELD_CONFIG[editingField] : null
    const popoverAnchorEl = editingField === EditableField.EMAIL ? emailAnchorRef.current : phoneAnchorRef.current

    return (
        <Box>
            <TakeawayCheckoutSectionHeader
                icon={faUser}
                title={
                    <LocalizedStrict id="takeaway-confirm-order-personal-info-title">
                        <span>Personal Information</span>
                    </LocalizedStrict>
                }
            />
            <Box className={classes.rows}>
                {name && (
                    <Box className={classes.row}>
                        <LocalizedStrict id="takeaway-confirm-order-personal-info-name-label">
                            <Typography variant="body1">Name:</Typography>
                        </LocalizedStrict>
                        <Typography variant="body1" className={classes.value}>
                            {name}
                        </Typography>
                    </Box>
                )}
                {email && (
                    <EditableInfoRow
                        labelNode={
                            <LocalizedStrict id="takeaway-confirm-order-personal-info-email-label">
                                <Typography variant="body1">Email:</Typography>
                            </LocalizedStrict>
                        }
                        value={email}
                        isActive={editingField === EditableField.EMAIL}
                        anchorRef={emailAnchorRef}
                        onClick={() => openEdit(EditableField.EMAIL, email)}
                    />
                )}
                {phone && (
                    <EditableInfoRow
                        labelNode={
                            <LocalizedStrict id="takeaway-confirm-order-personal-info-phone-label">
                                <Typography variant="body1">Phone Number:</Typography>
                            </LocalizedStrict>
                        }
                        value={phone}
                        isActive={editingField === EditableField.PHONE}
                        anchorRef={phoneAnchorRef}
                        onClick={() => openEdit(EditableField.PHONE, phone)}
                    />
                )}
            </Box>

            <LocalizedStrict
                id={activeConfig?.labelKey ?? "takeaway-confirm-order-personal-info-edit-email-field"}
                attrs={{ label: true }}
            >
                <StandardInlineEditPopover
                    open={editingField !== null}
                    anchorEl={popoverAnchorEl}
                    label="Save"
                    value={draftValue}
                    saveLabel={
                        <LocalizedStrict id="takeaway-confirm-order-personal-info-edit-save-button">
                            <span>Save</span>
                        </LocalizedStrict>
                    }
                    onChange={(next) => {
                        setDraftValue(next)
                        setShowError(false)
                    }}
                    onSave={saveEdit}
                    onClose={closeEdit}
                    error={showError ? draftError : undefined}
                    inputProps={activeConfig?.inputProps}
                />
            </LocalizedStrict>
        </Box>
    )
}
